import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Crown, Medal, Trophy } from 'lucide-react';
import { Snapshot, AVP } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { MovementIndicator } from './MovementIndicator';
interface SnapshotDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  snapshot: Snapshot | null;
  leaderboard: AVP[];
}
export function SnapshotDetailModal({
  isOpen,
  onClose,
  snapshot,
  leaderboard
}: SnapshotDetailModalProps) {
  if (!snapshot) return null;
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);
  return <AnimatePresence data-id="element-348">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} data-id="element-349" />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pointer-events-none overflow-y-auto" data-id="element-350">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} className="bg-[#141a2c] rounded-3xl my-8 max-w-5xl w-full shadow-2xl pointer-events-auto border border-white/10 relative overflow-hidden" data-id="element-351">
              {/* Locked / preserved ribbon */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-500/50 via-slate-300/50 to-slate-500/50" data-id="element-352" />

              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/5" data-id="element-353">
                <div data-id="element-354">
                  <div className="flex items-center gap-2 mb-2" data-id="element-355">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-500/10 border border-slate-500/20 text-slate-400 text-xs font-medium" data-id="element-356">
                      <Lock className="w-3 h-3" data-id="element-357" /> Finalized Snapshot
                    </div>
                    <span className="text-xs text-slate-500" data-id="element-358">
                      Locked {new Date(snapshot.lockedAt!).toLocaleDateString()}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white" data-id="element-359">
                    {snapshot.month} {snapshot.year} Leaderboard
                  </h2>
                  <p className="text-sm text-slate-400 mt-1" data-id="element-360">
                    Archived performance — preserved exactly as recorded at
                    month-end
                  </p>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1" data-id="element-361">
                  <X className="w-5 h-5" data-id="element-362" />
                </button>
              </div>

              {/* Summary KPIs */}
              <div className="grid grid-cols-3 gap-4 p-6 border-b border-white/5" data-id="element-363">
                <div className="bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-364">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-365">Total Volume</p>
                  <p className="text-xl font-bold text-white" data-id="element-366">
                    {formatCurrency(snapshot.totalVolume, true)}
                  </p>
                </div>
                <div className="bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-367">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-368">Total Revenue</p>
                  <p className="text-xl font-bold text-emerald-400" data-id="element-369">
                    {formatCurrency(snapshot.totalRevenue, true)}
                  </p>
                </div>
                <div className="bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-370">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-371">
                    Leaders Included
                  </p>
                  <p className="text-xl font-bold text-white" data-id="element-372">
                    {leaderboard.length}
                  </p>
                </div>
              </div>

              {/* Top 3 podium - compact, podium-ordered (2nd, 1st, 3rd) */}
              <div className="p-6 border-b border-white/5" data-id="element-373">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-374">
                  <Trophy className="w-4 h-4 text-yellow-400" data-id="element-375" /> Top Performers
                </h3>
                <div className="grid grid-cols-3 gap-3 items-end" data-id="element-376">
                  {[top3[1], top3[0], top3[2]].filter(Boolean).map(avp => {
                const isFirst = avp.rank === 1;
                const isSecond = avp.rank === 2;
                const cardStyle = isFirst ? 'bg-gradient-gold' : isSecond ? 'bg-gradient-silver' : 'bg-gradient-bronze';
                const Icon = isFirst ? Crown : Medal;
                const iconColor = isFirst ? 'text-yellow-400' : isSecond ? 'text-slate-300' : 'text-orange-400';
                const podiumOffset = isFirst ? '-translate-y-6' : isSecond ? '-translate-y-2' : '';
                return <div key={avp.id} className={`rounded-2xl p-4 border flex items-center gap-3 ${cardStyle} ${podiumOffset}`} data-id="element-377">
                        <div className="w-10 h-10 rounded-full bg-[#0f1423] border border-white/10 flex items-center justify-center text-sm font-bold shrink-0" data-id="element-378">
                          {avp.initials}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-379">
                          <div className="flex items-center gap-1" data-id="element-380">
                            <Icon className={`w-3.5 h-3.5 ${iconColor}`} data-id="element-381" />
                            <span className="text-xs text-slate-400" data-id="element-382">
                              #{avp.rank}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-white truncate" data-id="element-383">
                            {avp.name}
                          </p>
                          <p className="text-xs text-slate-400 truncate" data-id="element-384">
                            {formatCurrency(avp.totalRevenue, true)}
                          </p>
                        </div>
                      </div>;
              })}
                </div>
              </div>

              {/* Full table */}
              <div className="p-6 max-h-[400px] overflow-y-auto" data-id="element-385">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-386">
                  Full Standings
                </h3>
                <table className="w-full text-left" data-id="element-387">
                  <thead data-id="element-388">
                    <tr className="border-b border-white/10 text-xs uppercase text-slate-500" data-id="element-389">
                      <th className="py-2 pr-3" data-id="element-390">Rank</th>
                      <th className="py-2 pr-3" data-id="element-391">Leader</th>
                      <th className="py-2 pr-3" data-id="element-392">Division</th>
                      <th className="py-2 pr-3 text-right" data-id="element-393">Volume</th>
                      <th className="py-2 pr-3 text-right" data-id="element-394">Revenue</th>
                      <th className="py-2 pr-3 text-right" data-id="element-395">Loans</th>
                      <th className="py-2 text-right" data-id="element-396">Move</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-397">
                    {leaderboard.map(avp => <tr key={avp.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-398">
                        <td className="py-3 pr-3" data-id="element-399">
                          <span className={`font-semibold ${avp.rank <= 3 ? 'text-yellow-400' : 'text-slate-300'}`} data-id="element-400">
                            {avp.rank}
                          </span>
                        </td>
                        <td className="py-3 pr-3" data-id="element-401">
                          <div className="flex items-center gap-2" data-id="element-402">
                            <div className="w-7 h-7 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-semibold" data-id="element-403">
                              {avp.initials}
                            </div>
                            <span className="text-sm text-white font-medium" data-id="element-404">
                              {avp.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 pr-3 text-sm text-slate-400" data-id="element-405">
                          {avp.division}
                        </td>
                        <td className="py-3 pr-3 text-sm text-white text-right" data-id="element-406">
                          {formatCurrency(avp.loanVolume, true)}
                        </td>
                        <td className="py-3 pr-3 text-sm text-emerald-400 text-right" data-id="element-407">
                          {formatCurrency(avp.totalRevenue, true)}
                        </td>
                        <td className="py-3 pr-3 text-sm text-slate-300 text-right" data-id="element-408">
                          {formatNumber(avp.loansClosed)}
                        </td>
                        <td className="py-3 text-right" data-id="element-409">
                          <div className="flex justify-end" data-id="element-410">
                            <MovementIndicator currentRank={avp.rank} previousRank={avp.previousRank} data-id="element-411" />
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-white/5 flex items-center justify-between bg-black/10" data-id="element-412">
                <p className="text-xs text-slate-500 flex items-center gap-2" data-id="element-413">
                  <Lock className="w-3 h-3" data-id="element-414" />
                  This snapshot is permanently preserved and cannot be modified
                </p>
                <button onClick={onClose} className="px-4 py-2 rounded-lg glass-panel hover:bg-white/10 text-white text-sm font-medium transition-colors" data-id="element-415">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}